import { computed, makeObservable, observable } from 'mobx';

// Dropdown page size: how many rows are visible before "load more", and
// how many each "load more" reveals.
export const PAGE_SIZE = 10;
export const PAGE_STEP = 10;
// Default size of a `search` source's initial fetch. `total <= threshold`
// collapses the control to client-side filtering (hold the universe, no
// further requests); above it, the control stays server-side.
export const DEFAULT_THRESHOLD = 1000;

const matchesQuery = (label: string, query: string): boolean => label.toLowerCase().includes(query.toLowerCase());

/**
 * One mode of an EditReference's option list. The model swaps between
 * implementations as loading resolves (State pattern), so neither the
 * model nor the component branches on mode — each state encapsulates its
 * own `onSearch` / `loadMore` and the derived counts the footer reads.
 *
 * `options` is already sliced to the current page; "showing
 * `shownCount` of `filteredCount`" describes the footer, and
 * `canLoadMore` whether another page exists. `reset` returns to the
 * initial (empty-query) set; `findByKey` looks a value up in whatever the
 * state currently holds, so the model can hydrate a selection's label.
 */
export interface ReferenceState<T> {
  readonly options: readonly T[];
  readonly isLoading: boolean;
  readonly shownCount: number;
  readonly filteredCount: number;
  readonly canLoadMore: boolean;
  readonly emptyLabel: string;
  onSearch(query: string): void;
  loadMore(): void;
  reset(): void;
  findByKey(key: string | number): T | null;
}

/**
 * Pre-resolution state for `eager` / `search` sources. Holds nothing but
 * a buffer of the latest query the user typed while loading, so the
 * model can replay it once the real state takes over.
 */
export class LoadingState<T> implements ReferenceState<T> {
  private _buffered = '';

  constructor() {
    makeObservable<LoadingState<T>, '_buffered'>(this, { _buffered: observable });
  }

  get bufferedQuery(): string {
    return this._buffered;
  }
  get canLoadMore(): boolean {
    return false;
  }
  get emptyLabel(): string {
    return 'Loading…';
  }
  get filteredCount(): number {
    return 0;
  }
  get isLoading(): boolean {
    return true;
  }
  get options(): readonly T[] {
    return [];
  }
  get shownCount(): number {
    return 0;
  }

  onSearch = (query: string): void => {
    this._buffered = query;
  };
  loadMore = (): void => {};
  reset = (): void => {
    this._buffered = '';
  };
  findByKey = (_key: string | number): T | null => null;
}

/** Terminal state when the initial load rejected. */
export class ErrorState<T> implements ReferenceState<T> {
  constructor(private readonly _error: Error) {}

  get options(): readonly T[] {
    return [];
  }
  get isLoading(): boolean {
    return false;
  }
  get shownCount(): number {
    return 0;
  }
  get filteredCount(): number {
    return 0;
  }
  get canLoadMore(): boolean {
    return false;
  }
  get emptyLabel(): string {
    return `Error: ${this._error.message}`;
  }

  onSearch = (): void => {};
  loadMore = (): void => {};
  reset = (): void => {};
  findByKey = (_key: string | number): T | null => null;
}

/**
 * Whole universe held in memory; `onSearch` filters it case-insensitively
 * on the label and `loadMore` just reveals more rows. No network, ever —
 * used by `static`, `eager`, and a `search` source that collapsed to
 * client-side because its total fit under the threshold. Filtering is
 * synchronous, so there's no in-flight state to flicker through.
 */
export class ClientState<T> implements ReferenceState<T> {
  private _query = '';
  private _limit = PAGE_SIZE;

  constructor(
    private readonly _universe: readonly T[],
    private readonly _getKey: (item: T) => string | number,
    private readonly _getLabel: (item: T) => string
  ) {
    makeObservable<ClientState<T>, '_query' | '_limit' | '_filtered'>(this, {
      _query: observable,
      _limit: observable,
      _filtered: computed,
    });
  }

  get canLoadMore(): boolean {
    return this.shownCount < this.filteredCount;
  }
  get emptyLabel(): string {
    return 'No matches';
  }
  // Read by `options` and (via `filteredCount`) the count getters, so it's memoized to filter the universe once per render.
  private get _filtered(): readonly T[] {
    const q = this._query;
    if (q === '') return this._universe;
    return this._universe.filter((item) => matchesQuery(this._getLabel(item), q));
  }
  get filteredCount(): number {
    return this._filtered.length;
  }
  get isLoading(): boolean {
    return false;
  }
  get options(): readonly T[] {
    return this._filtered.slice(0, this._limit);
  }
  get shownCount(): number {
    return Math.min(this._limit, this.filteredCount);
  }

  onSearch = (query: string): void => {
    this._query = query;
    this._limit = PAGE_SIZE;
  };
  loadMore = (): void => {
    this._limit = this._limit + PAGE_STEP;
  };
  reset = (): void => {
    this._query = '';
    this._limit = PAGE_SIZE;
  };
  findByKey = (key: string | number): T | null => this._universe.find((item) => this._getKey(item) === key) ?? null;
}

/**
 * Server-side mode for a universe larger than the threshold. Holds the
 * page returned for the current query.
 *
 * A typed query is a *new* search: the held rows are cleared so the menu
 * shows "Searching…" rather than stale results, then replaced when the
 * response lands. `loadMore` is different — it keeps the current rows on
 * screen, slicing them client-side until it runs past them, only then
 * fetching a larger page. `reset` restores the initial empty-query page
 * without a round-trip.
 */
export class ServerState<T> implements ReferenceState<T> {
  private _items: readonly T[];
  private _total: number;
  private _query = '';
  private _limit = PAGE_SIZE;
  private _pending = false;
  private readonly _initialItems: readonly T[];
  private readonly _initialTotal: number;
  // Size of the last server page requested; load-more grows it by a
  // further `threshold` when it needs rows beyond what's held.
  private _fetchedLimit: number;
  // Monotonic guard so a slow earlier response (or one a reset/new-query
  // superseded) can't overwrite a newer one.
  private _seq = 0;

  constructor(
    initialItems: readonly T[],
    initialTotal: number,
    private readonly _search: (query: string, limit: number) => Promise<{ readonly items: readonly T[]; readonly total: number }>,
    private readonly _threshold: number,
    private readonly _getKey: (item: T) => string | number
  ) {
    this._items = initialItems;
    this._total = initialTotal;
    this._initialItems = initialItems;
    this._initialTotal = initialTotal;
    this._fetchedLimit = _threshold;
    makeObservable<ServerState<T>, '_items' | '_total' | '_query' | '_limit' | '_pending'>(this, {
      _items: observable.ref,
      _total: observable,
      _query: observable,
      _limit: observable,
      _pending: observable,
    });
  }

  get canLoadMore(): boolean {
    return this.shownCount < this._total;
  }
  get emptyLabel(): string {
    return this._pending ? 'Searching…' : 'No matches';
  }
  get filteredCount(): number {
    return this._total;
  }
  get isLoading(): boolean {
    return this._pending;
  }
  get options(): readonly T[] {
    return this._items.slice(0, this._limit);
  }
  get shownCount(): number {
    return Math.min(this._limit, this._items.length);
  }

  onSearch = (query: string): void => {
    if (query === this._query) return;
    this._query = query;
    this._limit = PAGE_SIZE;
    // New query → clear the held rows so the menu shows "Searching…"
    // instead of the previous query's (stale) results.
    this._items = [];
    this._total = 0;
    this._fetch(query, this._threshold);
  };

  loadMore = (): void => {
    const next = this._limit + PAGE_STEP;
    const held = this._items.length;
    this._limit = next;
    // Past what we hold but the server has more — grow the page. The rows
    // already on screen stay (no clear) while the larger page loads.
    if (next > held && held < this._total) {
      this._fetch(this._query, this._fetchedLimit + this._threshold);
    }
  };

  reset = (): void => {
    this._seq++; // invalidate any in-flight fetch
    this._query = '';
    this._limit = PAGE_SIZE;
    this._pending = false;
    this._items = this._initialItems;
    this._total = this._initialTotal;
    this._fetchedLimit = this._threshold;
  };

  // Look in the current page first, then the (larger) initial page — a
  // value selected before a search narrowed the rows is still found there
  // without a `resolve` round-trip.
  findByKey = (key: string | number): T | null =>
    this._items.find((item) => this._getKey(item) === key) ?? this._initialItems.find((item) => this._getKey(item) === key) ?? null;

  private _fetch = (query: string, limit: number): void => {
    const seq = ++this._seq;
    this._pending = true;
    this._search(query, limit).then(
      (res) => {
        if (seq !== this._seq) return;
        this._items = res.items;
        this._total = res.total;
        this._fetchedLimit = limit;
        this._pending = false;
      },
      () => {
        if (seq !== this._seq) return;
        this._pending = false;
      }
    );
  };
}
